/**
 * 常用正则
 * 来自https://github.com/yiminghe/async-validator/blob/master/src/rule/type.js
 */
const COMMON_PATTERNS = {
    MOBILE: /^1[3456789]{1}[0-9]{9}$/,
    EMAIL: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    URL: /^(?:(?:http|https|ftp):\/\/)(?:\S+(?::\S*)?@)?(?:(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,}))?)(?::\d{2,5})?(?:[/?#]\S*)?$/i,
    RELATIVE_URL: /^([/][a-z\u00a1-\uffff0-9-_.]+)+([/?#]\S*)?$/i
};

/**
 * 生成随机码
 * @param {*} len 
 */
function randomCode(len = 6) {
    let dictionary = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let code = '';
    for (let i = 1; i <= len; i++) {
        code += dictionary.charAt(Math.floor(Math.random() * dictionary.length));
    }
    return code;
}

/**
 * 深度拷贝
 * @param {*} obj 
 */
function deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
}

/**
 * 判断传入的值是否是对象
 * @param {*} val
 */
function isObject(val) {
    return (
        val != null && typeof val === 'object' && Array.isArray(val) === false
    );
}

/**
 * 判断传入的值是否是数组
 * @param {*} val
 */
function isArray(val) {
    return val != null && Array.isArray(val);
}

/**
 * 判断传入的值是否是空对象
 * @param {*} val 
 */
function isEmptyObject(val) {
    if (isObject(val)) {
        let keys = Object.keys(val);
        return keys.length == 0;
    }
    return false;
}

/**
 * 判断传入的字符串是否是email
 * @param {*} val 
 */
function isEmail(val) {
    return typeof(val) === 'string' && COMMON_PATTERNS.EMAIL.test(val);
}

/**
 * 判断传入的字符串是否是URL
 * @param {*} val 
 */
function isUrl(val) {
    return typeof(val) === 'string' && COMMON_PATTERNS.URL.test(val);
}

/**
 * 判断传入的字符串是否是相对URL
 * @param {*} val 
 */
function isRelativeUrl(val) {
    return typeof(val) === 'string' && COMMON_PATTERNS.RELATIVE_URL.test(val);
}

/**
 * 从Content-Disposition中提取文件名
 * @param {*} contentDisposition
 */
function parseFilename(contentDisposition) {
    return contentDisposition.split(';')[1].trim().split('filename=')[1].replace(/"/g, '');
}

/**
 * 让主线程休眠指定的时间(毫秒)
 * @param {*} time 
 */
function sleep(time) {
    return new Promise((resolve) => setTimeout(resolve, time));
}

/**
 * 将{ prop: 'createTime', order: 'descending' } 转成后台的Sort数据报文 {orders: createTime:desc}
 * @param {*} querySort 
 */
function mapSort(querySort) {
    let sort = {};
    if (isObject(querySort) && querySort.prop) {
        let prop = querySort.prop;
        let order = querySort.order == 'descending' ? 'desc' : 'asc';
        sort.orders = prop + ':' + order;
    }
    return sort;
}

/**
 * 根据Vue组件名称查找组件实例
 * @param {*} vm        - 起始查找的组件实例(一般是this.$root)
 * @param {*} name      - 组件名称
 */
function getVueInstanceByName(vm, name) {
    if (vm.$options.name == name) {
        return vm;
    }
    let children = vm.$children;
    if (children && children.length) {
        for (let child of children) {
            let result = getVueInstanceByName(child, name);
            if (result) {
                return result;
            }
        }
    }
}

/**
 * 根据Vue组件ref名称查找组件实例
 * @param {*} vm        - 起始查找的组件实例(一般是this.$root)
 * @param {*} ref       - 组件ref名称
 */
function getVueInstanceByRef(vm, ref) {
    let refVm = vm.$refs[ref];
    if (refVm) {
        return refVm;
    }
    let children = vm.$children;
    if (children && children.length) {
        for (let child of children) {
            let result = getVueInstanceByRef(child, ref);
            if (result) {
                return result;
            }
        }
    }
}

/**
 * 根据递归查找对应的菜单对象
 * @param {*} userNavMenuList   - 用户可访问的树结构菜单
 * @param {*} menuViewName      - 菜单路由视图名称
 */
function findMenuByViewName(userNavMenuList, menuViewName) {
    for (let menu of userNavMenuList) {
        if (menu.menuViewName == menuViewName) {
            return menu;
        }
        if (
            Array.isArray(menu.subMenuList) &&
            menu.subMenuList.length
        ) {
            let result = findMenuByViewName(
                menu.subMenuList,
                menuViewName
            );
            if (result) {
                return result;
            }
        }
    }
}

/**
 * 根据递归查找对应的菜单对象
 * @param {*} userNavMenuList   - 用户可访问的树结构菜单
 * @param {*} menuId            - 菜单ID
 */
function findMenuByMenuId(userNavMenuList, menuId) {
    for (let menu of userNavMenuList) {
        if (menu.menuId == menuId) {
            return menu;
        }
        if (
            Array.isArray(menu.subMenuList) &&
            menu.subMenuList.length
        ) {
            let result = findMenuByMenuId(
                menu.subMenuList,
                menuId
            );
            if (result) {
                return result;
            }
        }
    }
}

/**
 * 根据递归查找对应的菜单对象
 * @param {*} userNavMenuList   - 用户可访问的树结构菜单
 * @param {*} menuPath          - 菜单URL路径
 */
function findMenuByMenuPath(userNavMenuList, menuPath) {
    for (let menu of userNavMenuList) {
        if (menu.menuPath == menuPath) {
            return menu;
        }
        if (
            Array.isArray(menu.subMenuList) &&
            menu.subMenuList.length
        ) {
            let result = findMenuByMenuPath(
                menu.subMenuList,
                menuPath
            );
            if (result) {
                return result;
            }
        }
    }
}

export {
    COMMON_PATTERNS,
    randomCode,
    deepClone,
    isObject,
    isArray,
    isEmptyObject,
    isEmail,
    isUrl,
    isRelativeUrl,
    parseFilename,
    sleep,
    mapSort,
    getVueInstanceByName,
    getVueInstanceByRef,
    findMenuByViewName,
    findMenuByMenuId,
    findMenuByMenuPath
};